// InfoEditor
//
// Editor inline del bloque de informacion (type='info') que el portal muestra
// como hero al pie de la pagina de Formacion.
//
// Hay UN bloque por audiencia: el terapeuta y la institucion pueden leer textos
// distintos, y en K12 lo mismo con profesor e institucion. Por eso el
// componente recibe `audience` y la pagina renderiza tantos editores como
// audiencias tenga la vertical. Si no existe en BD, crea con POST; si existe,
// hace PATCH.

import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Upload,
  Button,
  Space,
  Alert,
  Empty,
  message,
  Typography,
} from "antd";
import {
  PictureOutlined,
  SaveOutlined,
  EditOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import styles from "./InfoEditor.module.scss";

const MAX_IMAGE_MB = 5;
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
];

const { Title, Paragraph } = Typography;

export function InfoEditor({
  info,
  accessToken,
  api,
  onSaved,
  loading,
  audience,
  title = "Información",
  titlePlaceholder = "Ej. Bienvenido a la plataforma",
  descriptionPlaceholder = "Texto de presentación que se mostrará en el portal.",
  emptyText = "Aún no se ha creado este bloque de información",
}) {
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Sincronizar form con info recibido / al entrar en modo edicion.
  useEffect(() => {
    if (editing) {
      form.setFieldsValue({
        title: info?.title || "",
        description: info?.description || "",
      });
      setNewImage(null);
      setNewImagePreview(null);
      setError(null);
    }
  }, [editing, info, form]);

  const handlePickImage = (picked) => {
    if (!ALLOWED_IMAGE_TYPES.includes(picked.type)) {
      message.error("Imagen no permitida");
      return false;
    }
    if (picked.size > MAX_IMAGE_MB * 1024 * 1024) {
      message.error(`Supera ${MAX_IMAGE_MB} MB`);
      return false;
    }
    setNewImage(picked);
    const reader = new FileReader();
    reader.onload = (e) => setNewImagePreview(e.target.result);
    reader.readAsDataURL(picked);
    return false;
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError(null);
      const payload = {
        title: values.title?.trim(),
        description: values.description?.trim(),
      };
      if (audience) payload.audiences = audience;
      if (newImage) payload.image = newImage;

      if (info && info._id) {
        const updated = await api.update(accessToken, info._id, payload);
        message.success("Información actualizada");
        onSaved?.(updated.resource || updated.data);
      } else {
        payload.type = "info";
        const created = await api.create(accessToken, payload);
        message.success("Información creada");
        onSaved?.(created.resource || created.data);
      }
      setEditing(false);
    } catch (e) {
      if (e?.errorFields) return;
      setError(e?.body?.message || e?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={
        <span className={styles.title}>
          <InfoCircleOutlined /> {title}
        </span>
      }
      extra={
        !editing && info ? (
          <Button
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
            disabled={loading}
          >
            Editar
          </Button>
        ) : null
      }
      loading={loading}
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo guardar"
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* Modo lectura */}
      {!editing && info ? (
        <div className={styles.readCard}>
          {info.image ? (
            <img src={info.image} alt={info.title} className={styles.readImg} />
          ) : null}
          <div className={styles.readContent}>
            <Title level={4} style={{ margin: 0 }}>
              {info.title}
            </Title>
            <Paragraph className={styles.readDesc}>
              {info.description}
            </Paragraph>
          </div>
        </div>
      ) : null}

      {/* Estado vacio: invitar a crear */}
      {!editing && !info ? (
        <Empty description={emptyText} style={{ padding: "24px 0" }}>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
          >
            Crear información
          </Button>
        </Empty>
      ) : null}

      {/* Modo edicion */}
      {editing ? (
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="title"
            label="Título"
            rules={[
              { required: true, message: "Pon un título" },
              { min: 3 },
              { max: 120 },
            ]}
          >
            <Input maxLength={120} placeholder={titlePlaceholder} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripción"
            rules={[
              { required: true, message: "Pon una descripción" },
              { min: 10 },
              { max: 2000 },
            ]}
          >
            <Input.TextArea
              rows={6}
              maxLength={2000}
              showCount
              placeholder={descriptionPlaceholder}
            />
          </Form.Item>

          <div className={styles.imageBlock}>
            <div className={styles.imageLabel}>Imagen destacada (opcional)</div>
            {info?.image && !newImagePreview ? (
              <img
                src={info.image}
                alt="actual"
                className={styles.imagePreview}
              />
            ) : null}
            {newImagePreview ? (
              <img
                src={newImagePreview}
                alt="nueva"
                className={styles.imagePreview}
              />
            ) : null}
            <Upload.Dragger
              multiple={false}
              showUploadList={false}
              beforeUpload={handlePickImage}
              accept={ALLOWED_IMAGE_TYPES.join(",")}
              style={{ padding: 8 }}
            >
              <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                <PictureOutlined />
              </p>
              <p className="ant-upload-text" style={{ fontSize: 13 }}>
                Click o arrastra para subir/reemplazar
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 11 }}>
                jpg/png/webp/gif · max {MAX_IMAGE_MB} MB
              </p>
            </Upload.Dragger>
          </div>

          <Space style={{ marginTop: 12 }}>
            <Button onClick={() => setEditing(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={submit}
            >
              {info ? "Guardar cambios" : "Crear"}
            </Button>
          </Space>
        </Form>
      ) : null}
    </Card>
  );
}
